import React, { useState } from 'react';
import axios from 'axios';
import './App.css';

function App() {
  const [url, setUrl] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleIdentify = async () => {
    if (!url) return;

    setLoading(true);
    setError('');
    setResult(null);

    try {
      // In development, we can point to local serverless offline or express wrapper
      // Let's assume we use the localhost:3000 proxy setup to localhost:3001 or directly to 3001
      // For now, let's just point to localhost:3001/api/identify if running locally
      // Or we can configure a proper env var.
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/identify';

      const response = await axios.post(apiUrl, { url });
      setResult(response.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to identify video. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-4">
      {/* Search Icon Badge */}
      <div className="mb-6">
        <div className="bg-rose-500 p-3 rounded-xl shadow-lg shadow-rose-500/20">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-6 h-6 text-white">
            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
          </svg>
        </div>
      </div>

      <header className="mb-10 text-center space-y-3">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
          YouTube Video Finder
        </h1>
        <p className="text-zinc-400 text-lg">
          Paste a YouTube URL to discover the movie or TV show name
        </p>
      </header>

      <div className="w-full max-w-2xl bg-zinc-900/50 border border-zinc-800 p-8 rounded-3xl shadow-2xl backdrop-blur-sm">
        <div className="space-y-2 mb-6">
          <label className="text-sm font-semibold text-zinc-300 ml-1">YouTube URL</label>
          <div className="relative">
            <input
              type="text"
              placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-4 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-rose-500/50 focus:border-rose-500 transition-all font-medium"
            />
          </div>
        </div>

        <button
          onClick={handleIdentify}
          disabled={loading}
          className={`w-full py-4 rounded-xl font-bold text-white shadow-lg transition-all flex items-center justify-center gap-2 text-lg ${loading
            ? 'bg-zinc-700 cursor-not-allowed text-zinc-400'
            : 'bg-rose-500 hover:bg-rose-600 hover:shadow-rose-500/25 active:scale-[0.98]'
            }`}
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Analyzing...
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
              </svg>
              Find Video
            </>
          )}
        </button>

        <div className="mt-8 text-center">
          <p className="text-zinc-500 text-sm">
            Works with standard YouTube URLs, short links, and embedded video links
          </p>
        </div>

        {error && (
          <div className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-200 text-center text-sm font-medium">
            {error}
          </div>
        )}

        {result && (
          <div className="mt-8 animate-fade-in text-left border-t border-zinc-800 pt-8">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">{result.title}</h2>
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 bg-zinc-800 rounded-full text-xs font-bold text-rose-400 uppercase tracking-wider">{result.type}</span>
                  <span className="text-zinc-400 font-medium">{result.year}</span>
                  {result.confidence && (
                    <span className="text-zinc-500 text-sm font-medium">{(result.confidence * 100).toFixed(0)}% Match</span>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-zinc-800/50 p-6 rounded-2xl border border-zinc-800/50">
              <p className="text-zinc-300 leading-relaxed text-lg">
                {result.summary}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
